import { useState } from 'react'
import { Plus, RotateCcw, Cloud } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { useToast } from '@/providers/UiProviders'
import { ErrorState, EmptyState } from '@/components/States'
import { fmtBytes } from './infraApi'
import { useLoad, RefreshBtn, Rows, Stat } from './infraShared'
import {
  fetchCdn,
  saveCdn,
  fetchBackups,
  createBackup,
  fetchDeploys,
  createDeploy,
  rollbackDeploy,
  IX,
  type CdnConfig,
  type BackupRow,
  type DeploysResp,
  type DeployRow,
} from './infraExtApi'

const inputCls = 'mt-1 block w-full rounded-md border border-border-default bg-surface-1 px-2 py-1.5 text-sm text-primary'
const CDN_PROVIDERS = ['none', 'cloudflare', 'bunny', 'fastly', 'custom']

/* ── A17.17 — CDN holati (config) ─────────────────────────────── */
export function InfraCdnScreen() {
  const { toast } = useToast()
  const { data, err, reload } = useLoad<CdnConfig>(fetchCdn)
  const [form, setForm] = useState<CdnConfig | null>(null)
  const [busy, setBusy] = useState(false)
  const cfg = form ?? data

  const save = async () => {
    if (!cfg) return
    setBusy(true)
    try {
      await saveCdn(cfg)
      toast(IX.cdnSaved, { tone: 'success' })
      setForm(null)
      reload()
    } catch {
      toast(IX.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }
  const upd = (patch: Partial<CdnConfig>) => setForm({ ...(cfg as CdnConfig), ...patch })

  return (
    <>
      <PageHeader title={IX.cdn} screenId="A17.17" description={IX.cdnDesc} actions={<RefreshBtn onClick={reload} />} />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        {err ? (
          <ErrorState code={err.code} onRetry={reload} />
        ) : !cfg ? (
          <Rows n={3} />
        ) : (
          <div className="mx-auto max-w-xl space-y-3">
            <div className="rounded-lg border border-border-default bg-surface-2 p-4">
              <div className="mb-3 flex items-center gap-2">
                <Cloud size={16} className="text-accent" />
                <span className="text-sm font-medium text-primary">{IX.cdn}</span>
                <span className={`ml-auto rounded px-1.5 py-0.5 text-xs ${cfg.enabled ? 'bg-success/10 text-success' : 'bg-surface-3 text-tertiary'}`}>
                  {cfg.enabled ? 'on' : 'off'}
                </span>
              </div>
              <div className="space-y-3">
                <label className="flex items-center gap-2 text-sm text-secondary">
                  <input type="checkbox" checked={cfg.enabled} onChange={(e) => upd({ enabled: e.target.checked })} />
                  {IX.cdnEnabled}
                </label>
                <label className="block text-xs text-tertiary">
                  {IX.cdnProvider}
                  <select className={inputCls} value={cfg.provider} onChange={(e) => upd({ provider: e.target.value })}>
                    {CDN_PROVIDERS.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block text-xs text-tertiary">
                  {IX.cdnBaseUrl}
                  <input className={inputCls} value={cfg.base_url} placeholder="https://cdn.example.com" onChange={(e) => upd({ base_url: e.target.value })} />
                </label>
                <label className="block text-xs text-tertiary">
                  {IX.cdnZone}
                  <input className={inputCls} value={cfg.zone} onChange={(e) => upd({ zone: e.target.value })} />
                </label>
                <label className="block text-xs text-tertiary">
                  {IX.notes}
                  <input className={inputCls} value={cfg.notes} onChange={(e) => upd({ notes: e.target.value })} />
                </label>
              </div>
              <button type="button" onClick={save} disabled={busy || !form} className="mt-4 rounded-md bg-accent px-4 py-2 text-sm text-white disabled:opacity-50">
                {IX.save}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

/* Backup/drill umumiy jadval + yozib qo'yish formasi. */
function BackupsView({ screenId, title, desc, kind, recordLabel }: { screenId: string; title: string; desc: string; kind: string; recordLabel: string }) {
  const { toast } = useToast()
  const { data, err, reload } = useLoad<BackupRow[]>((s) => fetchBackups(kind, s))
  const drill = kind === 'restore_drill'
  const [open, setOpen] = useState(false)
  const [f, setF] = useState({ kind: drill ? 'restore_drill' : 'db', status: 'success', location: '', size_bytes: 0, notes: '' })
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    setBusy(true)
    try {
      await createBackup(f)
      toast(IX.done, { tone: 'success' })
      setOpen(false)
      setF({ kind: drill ? 'restore_drill' : 'db', status: 'success', location: '', size_bytes: 0, notes: '' })
      reload()
    } catch {
      toast(IX.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PageHeader
        title={title}
        screenId={screenId}
        description={desc}
        actions={
          <div className="flex gap-2">
            <button type="button" onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-1 rounded-md bg-accent px-3 py-1 text-sm text-white">
              <Plus size={14} />
              {recordLabel}
            </button>
            <RefreshBtn onClick={reload} />
          </div>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <div className="mx-auto max-w-4xl space-y-3">
          {open && (
            <div className="grid grid-cols-2 items-end gap-3 rounded-lg border border-accent/40 bg-surface-2 p-4 sm:grid-cols-3">
              {!drill && (
                <label className="text-xs text-tertiary">
                  {IX.kind}
                  <select className={inputCls} value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}>
                    {['db', 'full', 'files'].map((k) => (
                      <option key={k} value={k}>
                        {k}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label className="text-xs text-tertiary">
                {IX.status}
                <select className={inputCls} value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
                  {['success', 'failed', 'running', 'pending'].map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-xs text-tertiary">
                {IX.size} (bytes)
                <input type="number" className={inputCls} value={f.size_bytes} onChange={(e) => setF({ ...f, size_bytes: Number(e.target.value) })} />
              </label>
              <label className="col-span-2 text-xs text-tertiary sm:col-span-3">
                {IX.location}
                <input className={inputCls} value={f.location} placeholder="b2://bucket/path" onChange={(e) => setF({ ...f, location: e.target.value })} />
              </label>
              <label className="col-span-2 text-xs text-tertiary sm:col-span-3">
                {IX.notes}
                <input className={inputCls} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
              </label>
              <button type="button" onClick={submit} disabled={busy} className="rounded-md bg-accent px-4 py-2 text-sm text-white disabled:opacity-50">
                {IX.save}
              </button>
            </div>
          )}

          {err ? (
            <ErrorState code={err.code} onRetry={reload} />
          ) : !data ? (
            <Rows />
          ) : data.length === 0 ? (
            <EmptyState title={IX.noBackups} />
          ) : (
            <div className="overflow-hidden rounded-lg border border-border-default">
              <table className="w-full text-left text-sm">
                <thead className="bg-surface-2 text-xs text-tertiary">
                  <tr>
                    <th className="px-4 py-2 font-medium">{IX.kind}</th>
                    <th className="px-4 py-2 font-medium">{IX.status}</th>
                    <th className="px-4 py-2 font-medium">{IX.location}</th>
                    <th className="px-4 py-2 text-right font-medium">{IX.size}</th>
                    <th className="px-4 py-2 text-right font-medium">{IX.started}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((b) => (
                    <tr key={b.id} className="border-t border-border-subtle">
                      <td className="px-4 py-2 font-mono text-secondary">{b.kind}</td>
                      <td className="px-4 py-2">
                        <span className={`rounded px-1.5 py-0.5 text-xs ${b.status === 'success' ? 'bg-success/10 text-success' : b.status === 'failed' ? 'bg-danger/10 text-danger' : 'bg-warning/10 text-warning'}`}>
                          {b.status}
                        </span>
                      </td>
                      <td className="max-w-xs truncate px-4 py-2 font-mono text-xs text-tertiary">{b.location || '—'}</td>
                      <td className="px-4 py-2 text-right tabular text-secondary">{b.size_bytes > 0 ? fmtBytes(b.size_bytes) : '—'}</td>
                      <td className="whitespace-nowrap px-4 py-2 text-right text-xs text-tertiary">{new Date(b.started_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

/* ── A17.19 — Backup holati ───────────────────────────────────── */
export function InfraBackupsScreen() {
  return <BackupsView screenId="A17.19" title={IX.backups} desc={IX.backupsDesc} kind="" recordLabel={IX.recordBackup} />
}

/* ── A17.20 — Tiklash testi (restore drill) ───────────────────── */
export function InfraRestoreDrillScreen() {
  return <BackupsView screenId="A17.20" title={IX.restoreDrill} desc={IX.restoreDrillDesc} kind="restore_drill" recordLabel={IX.recordDrill} />
}

/* Deploy statuslar/kind uchun rang. */
function deployBadge(status: string, kind: string): string {
  if (kind === 'rollback') return 'bg-warning/10 text-warning'
  if (status === 'failed') return 'bg-danger/10 text-danger'
  if (status === 'rolled_back') return 'bg-surface-3 text-tertiary'
  return 'bg-success/10 text-success'
}

/* ── A17.21 — Deploy tarixi ───────────────────────────────────── */
export function InfraDeployHistoryScreen() {
  const { toast } = useToast()
  const { data, err, reload } = useLoad<DeploysResp>(fetchDeploys)
  const [open, setOpen] = useState(false)
  const [f, setF] = useState({ version: '', git_sha: '', environment: 'production', status: 'success', notes: '' })
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    if (!f.version.trim()) {
      toast(IX.failed, { tone: 'error' })
      return
    }
    setBusy(true)
    try {
      await createDeploy(f)
      toast(IX.done, { tone: 'success' })
      setOpen(false)
      setF({ version: '', git_sha: '', environment: 'production', status: 'success', notes: '' })
      reload()
    } catch {
      toast(IX.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PageHeader
        title={IX.deploys}
        screenId="A17.21"
        description={IX.deploysDesc}
        actions={
          <div className="flex gap-2">
            <button type="button" onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-1 rounded-md bg-accent px-3 py-1 text-sm text-white">
              <Plus size={14} />
              {IX.recordDeploy}
            </button>
            <RefreshBtn onClick={reload} />
          </div>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <div className="mx-auto max-w-4xl space-y-3">
          {data && (
            <div className="grid grid-cols-2 gap-2.5">
              <Stat label={IX.buildVersion} value={data.build_version} />
              <Stat label={IX.schemaVersion} value={data.schema_version >= 0 ? String(data.schema_version) : '—'} />
            </div>
          )}
          {open && (
            <div className="grid grid-cols-2 items-end gap-3 rounded-lg border border-accent/40 bg-surface-2 p-4 sm:grid-cols-3">
              <label className="text-xs text-tertiary">
                {IX.version}
                <input className={inputCls} value={f.version} placeholder="1.7.32+73" onChange={(e) => setF({ ...f, version: e.target.value })} />
              </label>
              <label className="text-xs text-tertiary">
                {IX.gitSha}
                <input className={inputCls} value={f.git_sha} onChange={(e) => setF({ ...f, git_sha: e.target.value })} />
              </label>
              <label className="text-xs text-tertiary">
                {IX.status}
                <select className={inputCls} value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
                  {['success', 'failed'].map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
              <label className="col-span-2 text-xs text-tertiary sm:col-span-3">
                {IX.notes}
                <input className={inputCls} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
              </label>
              <button type="button" onClick={submit} disabled={busy} className="rounded-md bg-accent px-4 py-2 text-sm text-white disabled:opacity-50">
                {IX.save}
              </button>
            </div>
          )}

          {err ? (
            <ErrorState code={err.code} onRetry={reload} />
          ) : !data ? (
            <Rows />
          ) : data.deploys.length === 0 ? (
            <EmptyState title={IX.noDeploys} />
          ) : (
            <div className="overflow-hidden rounded-lg border border-border-default">
              <table className="w-full text-left text-sm">
                <thead className="bg-surface-2 text-xs text-tertiary">
                  <tr>
                    <th className="px-4 py-2 font-medium">{IX.version}</th>
                    <th className="px-4 py-2 font-medium">{IX.gitSha}</th>
                    <th className="px-4 py-2 font-medium">{IX.status}</th>
                    <th className="px-4 py-2 font-medium">{IX.deployedBy}</th>
                    <th className="px-4 py-2 text-right font-medium">{IX.deployedAt}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.deploys.map((d) => (
                    <tr key={d.id} className="border-t border-border-subtle">
                      <td className="px-4 py-2 font-mono text-primary">
                        {d.version}
                        {d.kind === 'rollback' && <span className="ml-2 rounded bg-warning/10 px-1.5 py-0.5 text-xs text-warning">rollback → {d.rollback_to}</span>}
                      </td>
                      <td className="px-4 py-2 font-mono text-xs text-tertiary">{d.git_sha ? d.git_sha.slice(0, 8) : '—'}</td>
                      <td className="px-4 py-2">
                        <span className={`rounded px-1.5 py-0.5 text-xs ${deployBadge(d.status, d.kind)}`}>{d.status}</span>
                      </td>
                      <td className="px-4 py-2 text-secondary">{d.deployed_by || '—'}</td>
                      <td className="whitespace-nowrap px-4 py-2 text-right text-xs text-tertiary">{new Date(d.deployed_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

/* ── A17.22 — Rollback ────────────────────────────────────────── */
export function InfraRollbackScreen() {
  const { toast } = useToast()
  const { data, err, reload } = useLoad<DeploysResp>(fetchDeploys)
  const [target, setTarget] = useState<DeployRow | null>(null)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)

  const doRollback = async () => {
    if (!target) return
    setBusy(true)
    try {
      await rollbackDeploy(target.id, note)
      toast(IX.rollbackDone, { tone: 'success' })
      setTarget(null)
      setNote('')
      reload()
    } catch {
      toast(IX.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  // Rollback nishoni bo'la oladigan relizlar (deploy, hali rolled_back emas).
  const candidates = data?.deploys.filter((d) => d.kind === 'deploy' && d.status !== 'rolled_back') ?? []

  return (
    <>
      <PageHeader title={IX.rollback} screenId="A17.22" description={IX.rollbackDesc} actions={<RefreshBtn onClick={reload} />} />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <div className="mx-auto max-w-3xl space-y-3">
          {err ? (
            <ErrorState code={err.code} onRetry={reload} />
          ) : !data ? (
            <Rows />
          ) : candidates.length === 0 ? (
            <EmptyState title={IX.noDeploys} description={IX.rollbackDesc} />
          ) : (
            <div className="overflow-hidden rounded-lg border border-border-default">
              <table className="w-full text-left text-sm">
                <thead className="bg-surface-2 text-xs text-tertiary">
                  <tr>
                    <th className="px-4 py-2 font-medium">{IX.version}</th>
                    <th className="px-4 py-2 font-medium">{IX.deployedAt}</th>
                    <th className="px-4 py-2 text-right font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {candidates.map((d, i) => (
                    <tr key={d.id} className="border-t border-border-subtle">
                      <td className="px-4 py-2 font-mono text-primary">
                        {d.version}
                        {i === 0 && <span className="ml-2 rounded bg-accent/10 px-1.5 py-0.5 text-xs text-accent">{IX.current}</span>}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-xs text-tertiary">{new Date(d.deployed_at).toLocaleString()}</td>
                      <td className="px-4 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => setTarget(d)}
                          disabled={i === 0}
                          className="inline-flex items-center gap-1 rounded-md border border-border-default px-2.5 py-1 text-xs text-secondary hover:text-warning disabled:opacity-40"
                          title={i === 0 ? IX.current : IX.rollbackTo}
                        >
                          <RotateCcw size={13} />
                          {IX.rollbackTo}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {target && (
            <div className="rounded-lg border border-warning/40 bg-surface-2 p-4">
              <p className="text-sm font-medium text-primary">
                {IX.rollbackConfirm} <span className="font-mono text-warning">{target.version}</span>
              </p>
              <input
                className={`${inputCls} mt-3`}
                value={note}
                placeholder={IX.rollbackNote}
                onChange={(e) => setNote(e.target.value)}
              />
              <div className="mt-3 flex gap-2">
                <button type="button" onClick={doRollback} disabled={busy} className="inline-flex items-center gap-1 rounded-md bg-warning px-4 py-2 text-sm text-white disabled:opacity-50">
                  <RotateCcw size={14} />
                  {IX.rollback}
                </button>
                <button type="button" onClick={() => setTarget(null)} className="rounded-md border border-border-default px-4 py-2 text-sm text-secondary">
                  {IX.cancel}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
